import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as AssignIcon,
  PersonRemove as UnassignIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ScreenshotMonitor as MonitorIcon,
} from '@mui/icons-material';
import { screenAPI, userAPI, locationAPI } from '../services/api';
import { toast } from 'react-toastify';

const SCREEN_SIZES = [
  { value: '1920x1080', label: '1920x1080 (Full HD)' },
  { value: '1280x720', label: '1280x720 (HD)' },
  { value: '3840x2160', label: '3840x2160 (4K)' },
];

const SCREEN_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'maintenance', label: 'Maintenance' },
];

const Screens = () => {
  const [screens, setScreens] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalScreens, setTotalScreens] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    size: '1920x1080',
    location: '',
    status: 'active',
    description: '',
  });
  const [assignUserId, setAssignUserId] = useState('');

  useEffect(() => {
    fetchScreens();
    fetchUsers();
    fetchLocations();
  }, [page, rowsPerPage, searchQuery]);

  const fetchScreens = async () => {
    try {
      setLoading(true);
      const response = await screenAPI.getAllScreens({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });
      setScreens(response.data.data.screens || []);
      setTotalScreens(response.data.data.total || 0);
    } catch (error) {
      console.error('Error fetching screens:', error);
      toast.error('Failed to load screens');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAllUsers({ limit: 100 });
      setUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getLocationsDropdown();
      setLocations(response.data.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
    }
  };

  const handleOpenDialog = (screen = null) => {
    if (screen) {
      setSelectedScreen(screen);
      // Map backend data to form format
      const sizeValue = screen.screenSize 
        ? `${screen.screenSize.width}x${screen.screenSize.height}`
        : '1920x1080';
      
      setFormData({
        name: screen.screenName || '',
        size: sizeValue,
        location: screen.location || '',
        status: screen.status || 'active',
        description: screen.description || '',
      });
    } else {
      setSelectedScreen(null);
      setFormData({
        name: '',
        size: '1920x1080',
        location: '',
        status: 'active',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedScreen(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name) {
        toast.error('Screen name is required');
        return;
      }

      if (!formData.location) {
        toast.error('Location is required');
        return;
      }

      // Parse screen size (e.g., "1920x1080" -> {width: 1920, height: 1080, label: "1920x1080 (Full HD)"})
      const sizeOption = SCREEN_SIZES.find(s => s.value === formData.size);
      const [width, height] = formData.size.split('x').map(Number);

      // Map form data to backend expected format
      const screenData = {
        screenName: formData.name,
        location: formData.location,
        description: formData.description,
        status: formData.status,
        screenSize: {
          width,
          height,
          label: sizeOption?.label || formData.size
        }
      };

      if (selectedScreen) {
        const response = await screenAPI.updateScreen(selectedScreen._id, screenData);
        toast.success('Screen updated successfully');
      } else {
        const response = await screenAPI.createScreen(screenData);
        toast.success('Screen created successfully');
      }

      handleCloseDialog();
      fetchScreens();
    } catch (error) {
      console.error('Error saving screen:', error);
      toast.error(error.response?.data?.message || 'Failed to save screen');
    }
  };

  const handleDeleteClick = (screen) => {
    setSelectedScreen(screen);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await screenAPI.deleteScreen(selectedScreen._id);
      toast.success('Screen deleted successfully');
      setOpenDeleteDialog(false);
      setSelectedScreen(null);
      fetchScreens();
    } catch (error) {
      console.error('Error deleting screen:', error);
      toast.error(error.response?.data?.message || 'Failed to delete screen');
    }
  };

  const handleToggleStatus = async (screen) => {
    try {
      await screenAPI.toggleScreenStatus(screen._id);
      toast.success(`Screen ${screen.status === 'active' ? 'deactivated' : 'activated'} successfully`);
      fetchScreens();
    } catch (error) {
      console.error('Error toggling screen status:', error);
      toast.error('Failed to update screen status');
    }
  };

  const handleAssignClick = (screen) => {
    setSelectedScreen(screen);
    // Use assignedUserId from backend
    const userId = screen.assignedUserId?._id || screen.assignedUserId || '';
    setAssignUserId(userId);
    setOpenAssignDialog(true);
  };

  const handleAssignSubmit = async () => {
    try {
      if (!assignUserId) {
        toast.error('Please select a user');
        return;
      }

      await screenAPI.assignScreen(selectedScreen._id, assignUserId);
      toast.success('Screen assigned successfully');
      setOpenAssignDialog(false);
      setAssignUserId('');
      fetchScreens();
    } catch (error) {
      console.error('Error assigning screen:', error);
      toast.error(error.response?.data?.message || 'Failed to assign screen');
    }
  };

  const handleUnassign = async (screenId) => {
    if (!window.confirm('Are you sure you want to unassign this screen?')) {
      return;
    }

    try {
      await screenAPI.unassignScreen(screenId);
      toast.success('Screen unassigned successfully');
      fetchScreens();
    } catch (error) {
      console.error('Error unassigning screen:', error);
      toast.error(error.response?.data?.message || 'Failed to unassign screen');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Screen Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
          <TextField
            size="small"
            placeholder="Search screens..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            sx={{
              flex: { xs: '1 1 auto', sm: 'initial' },
              maxWidth: { sm: 250 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.9rem',
                backgroundColor: '#fafbfc',
                '& fieldset': {
                  borderColor: '#d1d5db',
                },
                '&:hover fieldset': {
                  borderColor: '#9ca3af',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#7C3AED',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '0.9rem',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9ca3af', fontSize: '1.1rem', mr: 0.5 }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Refresh table" arrow>
            <IconButton
              size="small"
              onClick={fetchScreens}
              color="primary"
              sx={{
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(124, 58, 237, 0.08)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#7C3AED',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#6D28D9',
              },
            }}
          >
            Add Screen
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: 'background.default',
                '& .MuiTableCell-head': {
                  color: 'text.secondary',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  padding: '14px 16px',
                },
              }}
            >
              <TableCell sx={{ width: '18%' }}>Name</TableCell>
              <TableCell sx={{ width: '14%' }}>Size</TableCell>
              <TableCell sx={{ width: '16%' }}>Location</TableCell>
              <TableCell sx={{ width: '12%' }}>Status</TableCell>
              <TableCell sx={{ width: '20%' }}>Assigned To</TableCell>
              <TableCell sx={{ width: '12%' }}>Created</TableCell>
              <TableCell align="center" sx={{ width: '10%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : screens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <MonitorIcon sx={{ fontSize: 64, color: '#cbd5e1' }} />
                    <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600 }}>
                      No Screens Yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      No screens found. Add your first screen to get started.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              screens.map((screen) => (
                <TableRow 
                  key={screen._id}
                  sx={{
                    '&:hover': { backgroundColor: 'action.hover' },
                    transition: 'all 0.2s ease',
                    '& .MuiTableCell-root': {
                      padding: '12px 16px',
                      verticalAlign: 'middle',
                    },
                  }}
                >
                  <TableCell sx={{ width: '18%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {(screen.screenName || screen.name || '-').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: '14%', fontSize: '0.9rem', color: '#64748b' }}>
                    {screen.screenSize 
                      ? `${screen.screenSize.width}x${screen.screenSize.height}`
                      : screen.size || '-'}
                  </TableCell>
                  <TableCell sx={{ width: '16%', fontSize: '0.9rem', color: '#64748b' }}>
                    {(screen.location || '-').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </TableCell>
                  <TableCell sx={{ width: '12%' }}>
                    <Chip
                      icon={screen.status === 'active' ? <ActiveIcon /> : <InactiveIcon />}
                      label={screen.status === 'active' ? 'Active' : screen.status === 'inactive' ? 'Inactive' : 'Maintenance'}
                      size="small"
                      onClick={() => handleToggleStatus(screen)}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        backgroundColor: screen.status === 'active' ? '#dcfce7' : screen.status === 'maintenance' ? '#fef3c7' : '#f5f5f5',
                        color: screen.status === 'active' ? '#22c55e' : screen.status === 'maintenance' ? '#f59e0b' : '#9ca3af',
                        border: screen.status === 'active' ? '1px solid #86efac' : screen.status === 'maintenance' ? '1px solid #fcd34d' : '1px solid #e5e7eb',
                        height: '24px',
                        '& .MuiChip-icon': {
                          fontSize: '1rem !important',
                        },
                        '&:hover': {
                          backgroundColor: screen.status === 'active' ? '#bbf7d0' : screen.status === 'maintenance' ? '#fde68a' : '#f0f0f0',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '20%' }}>
                    {screen.assignedUserId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.9rem' }}>
                          {screen.assignedUserId.fullName || screen.assignedUserId.email}
                        </Typography>
                        <Tooltip title="Unassign screen" placement="top" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleUnassign(screen._id)}
                            sx={{
                              padding: '4px',
                              color: '#9ca3af',
                              '&:hover': {
                                backgroundColor: 'rgba(107, 114, 128, 0.08)',
                                color: '#6b7280',
                              },
                            }}
                          >
                            <UnassignIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 500 }}>
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '12%', fontSize: '0.9rem', color: '#64748b' }}>
                    {new Date(screen.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                  </TableCell>
                  <TableCell align="center" sx={{ width: '10%' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Assign User">
                        <IconButton
                          size="small"
                          onClick={() => handleAssignClick(screen)}
                        >
                          <AssignIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Screen">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(screen)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Screen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(screen)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalScreens}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#fafbfc',
            '& .MuiTablePagination-toolbar': {
              minHeight: '56px',
              padding: '8px 16px',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              margin: '0 !important',
              fontSize: '0.9rem',
            },
          }}
        />
      </TableContainer>

      {/* Create/Edit Screen Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedScreen ? 'Edit Screen' : 'Create New Screen'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Screen Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Screen Size"
              select
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              fullWidth
            >
              {SCREEN_SIZES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Location"
              select
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              fullWidth
              required
              helperText={locations.length === 0 ? "No locations available. Please create locations first." : ""}
            >
              {locations.length === 0 ? (
                <MenuItem value="" disabled>
                  No locations available
                </MenuItem>
              ) : (
                locations.map((location) => (
                  <MenuItem key={location._id} value={location.name}>
                    {location.name}
                  </MenuItem>
                ))
              )}
            </TextField>
            <TextField
              label="Status"
              select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              fullWidth
            >
              {SCREEN_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Optional description..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedScreen ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Screen to User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Screen: {selectedScreen?.name} ({selectedScreen?.size})
            </Typography>
            <TextField
              label="Select User"
              select
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>Select a user</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.fullName || user.username} ({user.email}) - {user.screenSize ? `${user.screenSize.width}x${user.screenSize.height}` : 'No screen size'}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              All users are shown. Screen size: {selectedScreen?.screenSize ? `${selectedScreen.screenSize.width}x${selectedScreen.screenSize.height}` : 'Unknown'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignSubmit} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete screen "{selectedScreen?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Screens;